import { element, replaceChildren } from '../ui/dom.js';

/**
 * Il rifiuto di una blocking function non arriva con un codice strutturato:
 * Identity Platform lo incapsula dentro `auth/internal-error`, con il messaggio
 * annegato nella stringa. Si riconosce dal marcatore, e all'utente si mostra
 * una frase comprensibile — mai l'errore grezzo.
 */
function isBlockingFunctionRejection(error) {
  const code = String(error?.code || '');
  if (code !== 'auth/internal-error') return false;
  return /BLOCKING_FUNCTION_ERROR_RESPONSE|permission-denied|failed-precondition/i
    .test(String(error?.message || ''));
}

function authErrorMessage(error) {
  const code = String(error?.code || '');
  if (/popup-closed-by-user|cancelled-popup-request/.test(code)) return '';
  if (isBlockingFunctionRejection(error)) {
    return 'Questo account Justbit non è abilitato ad A11yPilot. Chiedi l’abilitazione a chi gestisce il portale Justbit.';
  }
  if (/popup-blocked/.test(code)) return 'Il browser ha bloccato la finestra di accesso. Consenti i popup e riprova.';
  if (/operation-not-allowed/.test(code)) return 'L’accesso con Justbit non è attivo su questa installazione. Contatta chi gestisce il servizio.';
  if (/account-exists-with-different-credential/.test(code)) {
    return 'Esiste già un account con questa email creato con un altro metodo. Contatta un amministratore del workspace.';
  }
  if (/network-request-failed/.test(code)) return 'Connessione non disponibile. Controlla la rete e riprova.';
  if (/too-many-requests/.test(code)) return 'Troppi tentativi. Attendi qualche minuto prima di riprovare.';
  if (/user-disabled/.test(code)) return 'Questo account è stato disabilitato. Contatta il referente del workspace.';
  if (/invalid-email/.test(code)) return 'Inserisci un indirizzo email valido.';
  return 'Accesso non riuscito. Controlla le credenziali e riprova.';
}

export function renderSignedOut(root, { authClient, toast }) {
  const email = element('input', {
    className: 'auth-input',
    id: 'auth-email',
    name: 'email',
    type: 'email',
    inputmode: 'email',
    autocomplete: 'username',
    required: true,
  });
  const password = element('input', {
    className: 'auth-input',
    id: 'auth-password',
    name: 'password',
    type: 'password',
    autocomplete: 'current-password',
    required: true,
  });
  const error = element('p', { className: 'auth-message', role: 'alert', hidden: true });
  const submit = element('button', { className: 'auth-primary', type: 'submit' }, 'Accedi');
  /* L'`alt` non è opzionale: il logo È la parola «Justbit», e senza chi usa uno
     screen reader sentirebbe «Accedi con» e basta. */
  const justbit = authClient.justbitSsoEnabled
    ? element('button', { className: 'justbit-sso-button', type: 'button' }, [
        element('span', {}, 'Accedi con'),
        element('img', { src: '/justbit_logo_white.svg', alt: 'Justbit' }),
      ])
    : null;
  const reset = element('button', { className: 'auth-link', type: 'button' }, 'Password dimenticata?');
  const interactive = [email, password, submit, reset, ...(justbit ? [justbit] : [])];

  const showError = (message) => {
    error.textContent = message;
    error.hidden = !message;
  };
  const setBusy = (busy) => {
    interactive.forEach((control) => { control.disabled = busy; });
    submit.textContent = busy ? 'Accesso in corso…' : 'Accedi';
  };

  const form = element('form', {
    className: 'auth-form',
    noValidate: true,
    onSubmit: async (event) => {
      event.preventDefault();
      showError('');
      if (!email.checkValidity() || !password.value) {
        email.reportValidity();
        if (!password.value) password.focus();
        return;
      }
      setBusy(true);
      try {
        await authClient.signInWithEmail(email.value.trim(), password.value);
      } catch (signInError) {
        showError(authErrorMessage(signInError));
        setBusy(false);
      }
    },
  }, [
    element('div', { className: 'auth-field' }, [
      element('label', { for: 'auth-email' }, 'Email'),
      email,
    ]),
    element('div', { className: 'auth-field' }, [
      element('label', { for: 'auth-password' }, 'Password'),
      password,
    ]),
    error,
    submit,
  ]);

  justbit?.addEventListener('click', async () => {
    showError('');
    setBusy(true);
    try {
      await authClient.signInWithJustbit();
    } catch (signInError) {
      showError(authErrorMessage(signInError));
      setBusy(false);
    }
  });

  reset.addEventListener('click', async () => {
    showError('');
    if (!email.checkValidity()) {
      email.reportValidity();
      email.focus();
      return;
    }
    setBusy(true);
    try {
      await authClient.sendPasswordReset(email.value.trim());
      toast({ type: 'success', text: 'Se l’account esiste, riceverai le istruzioni per reimpostare la password.' });
    } catch (resetError) {
      const message = authErrorMessage(resetError);
      if (message) showError(message);
    } finally {
      setBusy(false);
    }
  });

  const heading = element('h1', { className: 'auth-title', tabindex: '-1' }, 'Accedi ad A11yPilot');
  const page = element('main', { className: 'auth-page', id: 'main-content' }, [
    element('section', { className: 'auth-card', 'aria-labelledby': 'auth-heading' }, [
      element('div', { className: 'auth-brand', 'aria-hidden': 'true' },
        element('img', {
          className: 'auth-brand__mark',
          src: '/assets/a11ypilot-mark.svg',
          alt: '',
          width: '200',
          height: '200',
        })),
      (() => { heading.id = 'auth-heading'; return heading; })(),
      element('p', { className: 'auth-subtitle' }, 'Audit automatici, evidenze e checklist operative per siti accessibili.'),
      /* Justbit è l'azione primaria e sta in cima: email e password sono il
         ripiego per chi in Justbit non c'è — clienti, esterni, account di
         servizio — e devono sembrarlo. */
      ...(justbit ? [justbit] : []),
      ...(justbit ? [element('div', { className: 'auth-divider' }, element('span', {}, 'oppure'))] : []),
      form,
      reset,
      element('p', { className: 'auth-footnote' }, 'Non è possibile registrarsi: l’account deve essere abilitato da un amministratore del workspace.'),
    ]),
  ]);

  replaceChildren(root, page);
  root.setAttribute('aria-busy', 'false');
  queueMicrotask(() => heading.focus());

  return () => replaceChildren(root);
}
